import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { Button, Modal, TextInput } from "carbon-components-react";
import { DocumentAdd16 } from "@carbon/icons-react";

import APIClient from "web_modules/APIClient";
import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";
import Validations from "web_modules/shared/FormHelpers/validation";

import { PIPELINE_PAGE_URL } from "web_modules/reporting_dashboard/PipelinePage";
import { convertToSlug } from "web_modules/reporting_dashboard/PipelinePage/helpers";
import { ViewsContext, View } from "web_modules/reporting_dashboard/PipelinePage/viewsContext";

import { HydratedAction } from "./shared";
import { dehydrateActions } from "./serializer";

const CreateNewView = ({
  actions,
  title,
  columns,
}: {
  actions: HydratedAction[];
  title: string;
  columns: string[];
}): Promise<View> => {
  const serializedActions = dehydrateActions(actions);

  return APIClient.post("/ro/pipeline_views", {
    serialized_actions: serializedActions,
    ordered_columns: columns,
    title,
  }).then(({ pipeline_view }) => {
    return pipeline_view;
  });
};

type Props = {
  originalTitle: string;
  actions: HydratedAction[];
  columns: string[];

  history: any;
  location: any;
  match: any;
};

const CreateNewViewModal: React.FunctionComponent<Props> = ({
  originalTitle,
  actions,
  columns,
  history,
}) => {
  const initialFormState = { title: `(copy) ${originalTitle}` };

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [formState, setFormState] = useState(initialFormState);
  const { handleTextInputChange } = getHandlers(
    formState,
    setFormState,
    invalidFields,
    setInvalidFields,
  );

  useEffect(() => {
    setFormState(initialFormState);
  }, [originalTitle]);

  const reset = () => {
    setModalIsOpen(false);
  };

  return (
    <ViewsContext.Consumer>
      {({ addView }) => (
        <>
          <Modal
            open={modalIsOpen}
            modalHeading="Save edits as a new view"
            primaryButtonText="Create new view"
            secondaryButtonText="Cancel"
            aria-label="Create new view"
            onBlur={reset}
            onRequestClose={reset}
            onRequestSubmit={async () => {
              if (invalidFields.length) {
                return;
              }

              const newView = await CreateNewView({ actions, columns, title: formState.title });

              setModalIsOpen(false);

              addView(newView);

              const { id, title: newTitle } = newView;
              const newSlug = convertToSlug(newTitle);
              const redirect = `${PIPELINE_PAGE_URL}/${id}/${newSlug}`;
              history.replace(redirect);
            }}
          >
            <TextInput
              id="title"
              name="title"
              value={formState.title}
              labelText="Title *"
              onChange={e => handleTextInputChange(e, "title", [Validations.IsRequired])}
              invalid={invalidFields.includes("title")}
              invalidText="Enter a valid title"
            />
          </Modal>

          <Button
            kind="ghost"
            onClick={() => setModalIsOpen(true)}
            renderIcon={DocumentAdd16}
            iconDescription="save edits as new view"
          >
            Save edits as new view
          </Button>
        </>
      )}
    </ViewsContext.Consumer>
  );
};

export default withRouter(CreateNewViewModal);
